import CryptoJS from "crypto-js";
import { ENCRYPTION_CONFIG } from "./decryptionConfig";

type Decryptable = Record<string, any>;

export class FieldDecryption {
    private key = CryptoJS.enc.Hex.parse(ENCRYPTION_CONFIG.KEY);
    private iv = CryptoJS.enc.Hex.parse(ENCRYPTION_CONFIG.IV);

    decrypt(encryptedText: string): string {
        if (!encryptedText || typeof encryptedText !== "string") {
            return encryptedText;
        }

        if (!/^[0-9a-fA-F]+$/.test(encryptedText)) {
            return encryptedText;
        }

        try {
            const encrypted = CryptoJS.enc.Hex.parse(encryptedText);
            const cipherParams = CryptoJS.lib.CipherParams.create({
                ciphertext: encrypted
            });

            const decrypted = CryptoJS.AES.decrypt(cipherParams, this.key, {
                iv: this.iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });

            const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
            return decryptedText || encryptedText;
        } catch (error) {
            console.warn("Decryption failed:", (error as Error).message);
            return encryptedText;
        }
    }

    isEncrypted(text: string): boolean {
        if (!text || typeof text !== "string") return false;
        return /^[0-9a-fA-F]+$/.test(text);
    }

    decryptObject<T extends Decryptable>(
        obj: T,
        fieldsToDecrypt: string[] = ["email", "fcmToken", "firebaseUID"]
    ): T {
        const decryptedObj: Record<string, any> = { ...obj };

        for (const field of fieldsToDecrypt) {
            const value = decryptedObj[field];
            if (typeof value === "string" && this.isEncrypted(value)) {
                decryptedObj[field] = this.decrypt(value);
            }
        }

        return decryptedObj as T;
    }

    decryptArray<T extends Decryptable>(
        array: T[],
        fieldsToDecrypt: string[] = ["email", "fcmToken", "firebaseUID"]
    ): T[] {
        return array.map((item) => this.decryptObject(item, fieldsToDecrypt));
    }

    decryptNestedObject<T = any>(
        obj: any,
        fieldsToDecrypt: string[] = ["email", "fcmToken", "firebaseUID"]
    ): T {
        if (!obj || typeof obj !== "object") return obj;

        if (Array.isArray(obj)) {
            return obj.map((item) =>
                typeof item === "object"
                    ? this.decryptNestedObject(item, fieldsToDecrypt)
                    : item
            ) as any;
        }

        const decryptedObj: Record<string, any> = {};

        for (const [key, value] of Object.entries(obj)) {
            if (value === null || value === undefined) {
                decryptedObj[key] = value;
            } else if (typeof value === "string") {
                if (fieldsToDecrypt.includes(key) && this.isEncrypted(value)) {
                    decryptedObj[key] = this.decrypt(value);
                } else {
                    decryptedObj[key] = value;
                }
            } else if (Array.isArray(value)) {
                decryptedObj[key] = this.decryptNestedObject(
                    value,
                    fieldsToDecrypt
                );
            } else if (typeof value === "object") {
                decryptedObj[key] = this.decryptNestedObject(
                    value,
                    fieldsToDecrypt
                );
            } else {
                decryptedObj[key] = value;
            }
        }

        return decryptedObj as T;
    }

    decryptData<T = any>(
        data: any,
        fieldsToDecrypt: string[] = ["email", "fcmToken", "firebaseUID"]
    ): T {
        if (Array.isArray(data)) {
            return data.map((item) =>
                typeof item === "object"
                    ? this.decryptNestedObject(item, fieldsToDecrypt)
                    : item
            ) as any;
        } else if (typeof data === "object" && data !== null) {
            return this.decryptNestedObject(data, fieldsToDecrypt);
        }

        return data;
    }

    decryptApiResponse<T = any>(
        response: any,
        fieldsToDecrypt: string[] = ["email", "fcmToken", "firebaseUID"]
    ): T {
        if (!response) return response;

        if ("data" in response && typeof response === "object") {
            return {
                ...response,
                data: this.decryptData(response.data, fieldsToDecrypt)
            };
        }

        return this.decryptData(response, fieldsToDecrypt);
    }
}

// Singleton instance
const fieldDecryption = new FieldDecryption();

export default fieldDecryption;
